<template>
  <div class="view-container">
    <div class="view-gabarit">
      <p class="europe-info">{{ $t('latLongEuropeInfo') }}</p>

      <VeeForm @submit="submitForm">
        <div class="view-row">
          <div class="view-description">
            <p class="baseline">{{ $t('specifyTheAddressOfCurrentSites') }}</p>
            <p class="description">{{ $t('identifiedActualSitesInCSVFile', currentSites.length, { count: currentSites.length }) }}</p>
          </div>

          <div class="view-content">
            <fieldset>
              <fieldset v-for="(site, idx) in currentSites" :key="idx">
                <label :for="`current-site-${site.id}`">{{ site.name }}</label>
                <div class="input-gabarit">
                  <Field
                    v-slot="{ field, errorMessage }"
                    :name="`current-site-${site.id}`"
                    :rules="{ required: addressInputIsRequired(site), is: !googleSuggested.currentSites[site.id] }"
                  >
                    <input
                      v-bind="field"
                      :id="`current-site-${site.id}`"
                      autocomplete="off"
                      type="text"
                      :class="{ 'has-error': !!errorMessage }"
                      :placeholder="$t('address')"
                      :value="site.address.input"
                      @input="e => inputOnChange(e, 'currentSites', idx, site.id)"
                    />

                    <span v-if="errorMessage && !(suggestions.currentSites[idx] && suggestions.currentSites[idx].length > 0)" class="error-caption">
                      {{ $t('errorMessage.fieldInvalid') }}
                    </span>
                  </Field>
                  <div v-if="suggestions.currentSites[idx] && suggestions.currentSites[idx].length > 0" class="autocomplete-dropdown">
                    <div
                      v-for="(suggestion, idx2) in suggestions.currentSites[idx]"
                      :key="idx2"
                      class="autocomplete-entry"
                      @click="() => updateAddress(idx, 'currentSites', site.id, suggestion.description)"
                    >
                      {{ suggestion.description }}
                    </div>
                  </div>

                  <span class="and-or">{{ $t('andOr') }}</span>

                  <div class="input-flex">
                    <div>
                      <span>{{ $t('latitude') }}</span>
                      <input id="latitude" v-model="site.address.lattitude" type="text" name="" @input="formatLongLat(site.address, idx, 'currentSites')" />
                    </div>
                    <div>
                      <span>{{ $t('longitude') }}</span>
                      <input id="longitude" v-model="site.address.longitude" type="text" name="" @input="formatLongLat(site.address, idx, 'currentSites')" />
                    </div>
                  </div>
                  <span v-if="site.address.input && site.address.lattitude && site.address.longitude" class="address-warning">
                    {{ $t('studyCreation.bothAddressAndLongLat') }}
                  </span>
                </div>
              </fieldset>
            </fieldset>
          </div>
        </div>
        <div class="view-row">
          <div class="view-description">
            <p class="baseline">{{ $t('addTargetSites') }}</p>
            <p class="description">{{ $t('addTargetSitesBaseline') }}</p>
            <Transition name="fade" mode="out-in">
              <div v-if="!sameCoverage" class="error-container">
                <div class="icon-container">
                  <i class="icon-alert"></i>
                </div>
                <div class="error-info">
                  <div class="error-title">{{ $t('studyCreation.carOnlyTitle') }}</div>
                  <div class="error-detail">{{ $t('studyCreation.carOnlyDetail') }}</div>
                </div>
              </div>
            </Transition>
          </div>
          <div class="view-content">
            <fieldset>
              <fieldset v-for="(site, idx) in targetSites" :key="idx">
                <label :for="`target-site-${idx}`">
                  {{ $t('targetSite') }} {{ idx + 1 }}
                  <i v-if="!site.id" class="icon-basket" @click.prevent.stop="() => removeSite(idx)"></i>
                  <i v-else-if="rawStudy && rawStudy.need_refresh" class="icon-basket" @click.prevent.stop="() => deleteSite(idx, site.id)"></i>
                </label>
                <div class="site-container">
                  <div class="input-container">
                    <div class="input-gabarit">
                      <Field v-slot="{ handleChange, errorMessage }" :name="`target-site-name-${site.id}`" rules="required">
                        <input
                          :id="`target-site-name-${site.id}`"
                          autocomplete="off"
                          :class="{ 'has-error': !!errorMessage }"
                          type="text"
                          :value="site.name"
                          :placeholder="$t('siteName')"
                          @input="
                            e => {
                              site.name = e.target.value;
                              handleChange(e);
                            }
                          "
                        />

                        <span v-if="errorMessage" class="error-caption">{{ $t('errorMessage.fieldInvalid') }}</span>
                      </Field>
                    </div>
                    <div class="input-gabarit">
                      <Field
                        v-slot="{ field, errorMessage }"
                        :name="`target-site-${site.id}`"
                        :rules="{ required: addressInputIsRequired(site), is: !googleSuggested.targetSites[site.id] }"
                      >
                        <input
                          v-bind="field"
                          :id="`target-site-${site.id}`"
                          autocomplete="off"
                          type="text"
                          :class="{ 'has-error': !!errorMessage }"
                          :placeholder="$t('address')"
                          :value="site.address.input"
                          @input="e => inputOnChange(e, 'targetSites', idx, site.id)"
                        />
                        <span v-if="errorMessage && !(suggestions.targetSites[idx] && suggestions.targetSites[idx].length > 0)" class="error-caption">
                          {{ $t('errorMessage.fieldInvalid') }}
                        </span>
                      </Field>
                      <div v-if="suggestions.targetSites[idx] && suggestions.targetSites[idx].length > 0" class="autocomplete-dropdown">
                        <div
                          v-for="(suggestion, idx2) in suggestions.targetSites[idx]"
                          :key="idx2"
                          class="autocomplete-entry"
                          @click="() => updateAddress(idx, 'targetSites', site.id, suggestion.description)"
                        >
                          {{ suggestion.description }}
                        </div>
                      </div>
                    </div>
                    <div v-if="isIE" class="input-gabarit">
                      <input type="file" name="thumbnail" accept="image/*" @change="uploadImage($event.target.files, site)" />
                    </div>

                    <span class="and-or">{{ $t('andOr') }}</span>

                    <div class="input-flex">
                      <div>
                        <span>{{ $t('latitude') }}</span>
                        <input id="latitude" v-model="site.address.lattitude" type="text" name="" @input="formatLongLat(site.address, idx, 'targetSites')" />
                      </div>
                      <div>
                        <span>{{ $t('longitude') }}</span>
                        <input id="longitude" v-model="site.address.longitude" type="text" name="" @input="formatLongLat(site.address, idx, 'targetSites')" />
                      </div>
                    </div>
                    <span v-if="site.address.input && site.address.lattitude && site.address.longitude" class="address-warning">
                      {{ $t('studyCreation.bothAddressAndLongLat') }}
                    </span>
                  </div>

                  <div v-if="!isIE" class="site-picture" :style="{ 'background-image': `url(${site.thumbnail})` }">
                    <input class="picture-input" type="file" name="thumbnail" accept="image/*" @change="uploadImage($event.target.files, site)" />
                    <div v-if="site.thumbnail" class="remove" @click.prevent="deleteImage(site)">
                      <i class="icon-basket"></i>
                    </div>
                    <i v-if="!site.thumbnail && !site.thumbnailLoading" class="icon-plusdownload"></i>
                    <div v-if="site.thumbnailLoading" class="loader" :style="{ width: `${Math.max(loading, 10)}%` }"></div>
                  </div>
                  <div v-else class="site-picture site-picture-ie" :style="{ 'background-image': `url(${site.thumbnail})` }">
                    <div v-if="site.thumbnail" class="remove" @click.prevent="deleteImage(site)">
                      <i class="icon-basket"></i>
                    </div>
                  </div>
                </div>
              </fieldset>
              <fieldset v-if="targetSites.length < 10">
                <button class="thumb-button" @click.prevent.stop="() => addSite()"><span>+</span> {{ $t('addASite') }}</button>
              </fieldset>
            </fieldset>
            <fieldset>
              <button class="study-button main" type="submit">{{ $t('validate') }}</button>
            </fieldset>
          </div>
        </div>
      </VeeForm>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Field, Form as VeeForm } from 'vee-validate';

export default {
  components: { Field, VeeForm },

  data() {
    return {
      currentSites: [],
      targetSites: [],
      suggestions: {
        targetSites: [],
        currentSites: [],
      },
      googleSuggested: {
        targetSites: {},
        currentSites: {},
      },
      currentCarOnly: false,
      targetCarOnly: false,
      sameCoverage: true,
      loading: 0,
    };
  },

  computed: {
    ...mapGetters({
      rawStudy: 'rawStudy',
    }),

    isIE() {
      const ua = navigator.userAgent;
      /* MSIE used to detect old browsers and Trident used to newer ones */
      return ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;
    },

    totalAddresses() {
      return 100;
    },

    status() {
      return 'Not good';
    },
  },

  mounted() {
    const id = this.$route.params.studyId;
    this.$store
      .dispatch('getCurrentSites', {
        id,
      })
      .then(response => {
        this.currentSites = response.data
          .filter(item => item.type === 1)
          .map(item => {
            const { id, address, name } = item;
            if (address.input) {
              this.googleSuggested.currentSites[id] = true;
            }
            if (!address.input && address.formatted) {
              address.input = address.formatted;
              this.googleSuggested.currentSites[id] = true;
            }
            return {
              id,
              address,
              name,
            };
          });
        this.targetSites = response.data
          .filter(item => item.type === 2)
          .map(item => {
            const { id, address, name, picture_path } = item;
            if (address.input) {
              this.googleSuggested.targetSites[id] = true;
            }
            if (!address.input && address.formatted) {
              address.input = address.formatted;
              this.googleSuggested.targetSites[id] = true;
            }
            return {
              id,
              address,
              name,
              thumbnail: picture_path,
              thumbnailLoading: false,
            };
          });
        if (this.targetSites.length === 0) {
          this.addSite();
        }
      });

    this.gMaps = google.maps;
    this.sessionToken = new this.gMaps.places.AutocompleteSessionToken();
  },

  methods: {
    inputOnChange(e, siteType, idx, id) {
      const { value } = e.target;
      this[siteType][idx].address.input = value;
      this.googleSuggested[siteType][id] = false;
      if (value.length > 5) {
        this.getQueryPredictions(value, idx, siteType);
      } else {
        this.suggestions[siteType][idx] = [];
      }
    },

    formatLongLat(addressObject, idx, siteType) {
      addressObject.lattitude = String(addressObject.lattitude)
        .replace(/[a-zA-Z]/g, '')
        .replace(/,/g, '.');
      addressObject.longitude = String(addressObject.longitude)
        .replace(/[a-zA-Z]/g, '')
        .replace(/,/g, '.');

      if (this.rawStudy.is_draft && !this[siteType][idx].userEdited) {
        this[siteType][idx].address.input = null;
        this[siteType][idx].userEdited = true;
      }
    },

    getQueryPredictions(input, idx, siteType) {
      const { sessionToken } = this;
      let service = new this.gMaps.places.AutocompleteService();
      service.getQueryPredictions(
        {
          input,
          sessionToken,
        },
        (predictions, status) => this.displaySuggestions(predictions, status, idx, siteType),
      );
    },

    displaySuggestions(predictions, status, idx, siteType) {
      if (status !== this.gMaps.places.PlacesServiceStatus.OK) {
        this.suggestions[siteType][idx] = [];
        return;
      }
      this.suggestions[siteType][idx] = predictions;
    },

    updateAddress(idx, siteType, id, value) {
      this.suggestions[siteType][idx] = [];

      this[siteType][idx].address.input = value;
      if (this.rawStudy.is_draft && !this[siteType][idx].userEdited) {
        this[siteType][idx].address.lattitude = null;
        this[siteType][idx].address.longitude = null;
        this[siteType][idx].userEdited = true;
      }

      this.googleSuggested[siteType][id] = true;

      const addresses = [...this.currentSites.map(site => site.address.input), ...this.targetSites.map(site => site.address.input)];
      this.$store.dispatch('checkCoverage', { addresses: addresses }).then(result => {
        this.sameCoverage = result.same_coverage;
      });
    },

    addSite() {
      if (this.targetSites.length < 10) {
        this.targetSites.push({
          name: '',
          address: {
            input: '',
            lattitude: '',
            longitude: '',
          },
          thumbnail: null,
          thumbnailLoading: false,
        });
      }
    },

    uploadImage(files, site) {
      site.thumbnailError = false;
      if (files.length <= 0) {
        return;
      }
      const file = files[0];
      site.thumbnailLoading = true;

      const data = new FormData();
      data.append('picture', file);

      this.loading = 0;
      site.thumbnailLoading = true;
      this.$store
        .dispatch('uploadSiteImage', { file: data, onUploadProgress: this.onUploadProgress })
        .then(result => {
          site.thumbnailLoading = false;
          site.thumbnail = result.data.url;
        })
        .catch(() => {
          site.thumbnailLoading = false;
        });
    },

    deleteImage(site) {
      site.thumbnail = null;
    },

    removeSite(idx) {
      this.targetSites.splice(idx, 1);
    },

    deleteSite(idx, id) {
      this.$store.dispatch('deleteSite', id);
      this.targetSites.splice(idx, 1);
    },

    submitForm() {
      const sites = [
        ...this.currentSites.map(item => {
          const { id } = item;
          return {
            id,
            address_input: item.address.input,
            latitude: item.address.lattitude ? Number(item.address.lattitude) : null,
            longitude: item.address.longitude ? Number(item.address.longitude) : null,
          };
        }),
        ...this.targetSites
          .filter(item => item.id)
          .map(item => {
            const { id, name } = item;
            return {
              id,
              name,
              address_input: item.address.input,
              picture_path: item.thumbnail,
              latitude: item.address.lattitude ? Number(item.address.lattitude) : null,
              longitude: item.address.longitude ? Number(item.address.longitude) : null,
            };
          }),
      ];
      const newSites = this.targetSites
        .filter(item => !item.id)
        .map(item => {
          const { name } = item;
          return {
            name,
            address_input: item.address.input ? item.address.input : '',
            latitude: item.address.lattitude ? Number(item.address.lattitude) : null,
            longitude: item.address.longitude ? Number(item.address.longitude) : null,
            picture_path: item.thumbnail,
            type: 2,
          };
        });
      const promises = [];
      promises.push(this.$store.dispatch('patchSites', { sites }));
      if (newSites.length > 0) {
        const id = this.$route.params.studyId;
        promises.push(this.$store.dispatch('postSites', { id, sites: newSites }));
      }
      Promise.all(promises).then(() => {
        const id = this.$route.params.studyId;
        this.$store
          .dispatch('geocode', { id })
          .then(() => this.nextStep())
          .catch(error => console.log(error));
      });
    },

    addressInputIsRequired(site) {
      return !site.address.lattitude && !site.address.longitude;
    },

    nextStep() {
      this.$router.push({ name: 'studyEditAddresses' });
    },

    onUploadProgress(progressEvent) {
      this.loading = Math.min(Math.round((progressEvent.loaded * 100) / progressEvent.total), 100);
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

label {
  i {
    cursor: pointer;
    &:hover {
      color: #bc141a;
    }
  }
}
.thumb-button {
  box-sizing: border-box;
  background-color: transparent;
  border: 2px solid transparent;
  font-weight: 600;
  color: colors.$red-active-color;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 14px;
  user-select: none;
  display: flex;
  justify-content: center;
  align-items: center;
  span {
    font-size: 1.25em;
    margin-right: 0.3em;
  }
  &:hover {
    border-color: colors.$red-active-color;
  }
}
.input-gabarit {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.input-flex {
  display: flex;
  gap: 1rem;
}
.autocomplete-dropdown {
  border: 1px solid #e3e3e3;

  .autocomplete-entry {
    line-height: 2;
    padding: 0 0.5em;
    cursor: pointer;

    & + .autocomplete-entry {
      border-top: 1px solid #e3e3e3;
    }
  }
}

.site-container {
  display: flex;
  width: 100%;

  .input-container {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .site-picture {
    position: relative;
    flex-shrink: 0;
    width: 128px;
    height: 96px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 0 0 16px;
    background-color: #cacaca;
    color: #ffffff;
    border-radius: 8px;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
    overflow: hidden;
    transition: 0.4s ease;
    cursor: pointer;

    &.site-picture-ie {
      cursor: default;
    }

    &:hover:not(.site-picture-ie) {
      background-color: #737379;

      .icon-plusdownload {
        transform: scale(1.1);
      }
    }

    .remove {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: rgba(0, 0, 0, 0.4);
      opacity: 0;
      transition: 0.4s ease;

      &:hover {
        opacity: 1;
      }

      .icon-basket {
        pointer-events: none;
        font-size: 48px;
      }
    }

    .picture-input {
      opacity: 0;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      cursor: pointer;
    }

    .icon-plusdownload {
      pointer-events: none;
      font-size: 64px;
      transition: 0.4s ease;
    }

    .loader {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 4px;
      border-radius: 2px;
      background: colors.$main-color;
      transition: 0.4s ease;
    }
  }
}

.address-warning {
  font-size: 0.8rem;
}

.error-container {
  width: 100%;
  margin: 16px auto;
  padding: 8px 16px 12px;
  overflow: hidden;
  display: flex;
  color: colors.$red-color;
  background: rgba(colors.$red-color, 0.1);
  border: solid 2px colors.$red-color;
  border-radius: 4px;
  box-sizing: border-box;
  transition: 0.4s ease;

  .icon-container {
    margin: 0 8px 0 0;
    font-size: 16px;
  }

  .error-title {
    font-weight: 600;
    font-size: 16px;
  }

  .error-detail {
    font-size: 14px;
  }
}

.europe-info {
  color: colors.$nude-concrete-color;
  margin-bottom: 40px;
}

.error-caption {
  position: relative;
}

.and-or {
  color: colors.$nude-concrete-color;
  text-align: center;
}

fieldset input[type='text'] {
  margin-bottom: 0;
}
</style>
